// Nigeria's real national border, simplified — [lat, lng] pairs, 58 points.
// Source: johan/world.geo.json (public-domain country boundary data, widely
// reused in open-source mapping projects; NGA.geo.json). Used by both
// Regional Performance maps (cxo-dashboard/index.tsx's RegionsBirdsEye and
// campaigns/tabs/regions-tab.tsx's RegionsTab) so the drawn outline and the
// projected region markers share one real coordinate system and bounding
// box — per direct ask, a recognizable Nigeria shape rather than the earlier
// abstract gradient-blob backdrop, still without a mapping library/GeoJSON
// runtime dependency (this is just a coordinate array baked in at build time).
export const NIGERIA_OUTLINE: [number, number][] = [
  [4.771983, 8.500288], [4.412108, 7.462108], [4.464689, 7.082596], [4.240594, 6.698072], [4.262453, 5.898173], [4.887971, 5.362805], [5.611802, 5.033574], [6.270651, 4.325607], [6.2583, 3.57418], [6.258817, 2.691702], [7.870734, 2.749063], [8.506845, 2.723793], [9.137608, 2.912308], [9.444153, 3.220352], [10.06321, 3.705438], [10.332186, 3.60007], [10.734746, 3.797112], [11.327939, 3.572216], [11.660167, 3.61118], [12.552903, 3.680634], [12.956109, 3.967283], [13.531216, 4.107946], [13.747482, 4.368344], [13.865924, 5.443058], [13.492768, 6.445426], [13.115091, 6.820442], [13.098038, 7.330747], [13.343527, 7.804671], [12.826659, 9.014933], [12.851102, 9.524928], [13.277252, 10.114814], [13.246918, 10.701032], [13.387323, 10.989593], [13.32898, 11.527803], [13.037189, 12.302071], [13.596147, 13.083987], [13.556356, 13.318702], [12.461565, 13.995353], [12.483657, 14.181336], [12.085361, 14.577178], [11.904752, 14.468192], [11.572369, 14.415379], [10.798566, 13.57295], [10.160362, 13.308676], [9.640626, 13.1676], [9.417772, 12.955468], [8.717763, 12.753672], [8.305824, 12.218872], [7.799808, 12.063946], [7.397042, 11.839309], [6.981383, 11.745774], [6.644427, 11.058788], [7.055358, 10.497375], [7.03877, 10.118277], [6.453482, 9.522706], [6.444491, 9.233163], [5.479666, 8.757533], [4.771983, 8.500288],
];

// Real bounding box computed from the outline above — the same bounds used
// to project both the outline and every region marker, so a marker's
// position stays consistent with the drawn coastline/borders instead of two
// independently-sourced, potentially mismatched coordinate systems.
export const NG_BOUNDS = { minLat: 4.24, maxLat: 13.87, minLng: 2.69, maxLng: 14.58 };

export function projectLatLng(lat: number, lng: number, width: number, height: number, padding = 0): { x: number; y: number } {
  const x = ((lng - NG_BOUNDS.minLng) / (NG_BOUNDS.maxLng - NG_BOUNDS.minLng)) * width;
  const y = (1 - (lat - NG_BOUNDS.minLat) / (NG_BOUNDS.maxLat - NG_BOUNDS.minLat)) * height;
  return { x: Math.max(padding, Math.min(width - padding, x)), y: Math.max(padding, Math.min(height - padding, y)) };
}

export function nigeriaOutlinePath(width: number, height: number): string {
  return (
    NIGERIA_OUTLINE.map(([lat, lng], i) => {
      const { x, y } = projectLatLng(lat, lng, width, height);
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(" ") + " Z"
  );
}

// Shortens a region name for tight map labels — every state's real name
// carries a generic " State" suffix ("Lagos State", "Kano State", ...)
// except the FCT, whose full "Federal Capital Territory" is long enough on
// its own to be worth its own common abbreviation rather than a suffix trim.
export function shortRegionName(raw: string): string {
  if (!raw) return raw;
  if (/federal capital territory/i.test(raw)) return "FCT";
  return raw.replace(/\s+state$/i, "");
}

// Every regions endpoint (overview /cxo-dashboard/regions and the campaign/
// adset/ad `/{id}/regions` ones) now rates each region itself:
// `performance: { rating, color, hex, basis, vs_average_pct }` — e.g.
// "moderate" / "#eab308" on "cost_per_result", 6.7% vs the average. Both maps
// colour markers, badges and legends straight off that rather than deriving a
// tier client-side. Missing/null `performance` → null, shown as gray "N/A".
export interface RegionPerformance {
  rating: string;
  label: string;
  hex: string;
  basis: string | null;
  vsAveragePct: number | null;
}

export const NO_PERFORMANCE_HEX = "#9ca3af";

export function regionPerformance(raw: any): RegionPerformance | null {
  const p = raw?.performance;
  if (!p || !p.rating) return null;
  const rating = String(p.rating).toLowerCase();
  const vs = Number(p.vs_average_pct);
  return {
    rating,
    label: rating.charAt(0).toUpperCase() + rating.slice(1),
    hex: p.hex || NO_PERFORMANCE_HEX,
    basis: p.basis ? String(p.basis).replace(/_/g, " ") : null,
    vsAveragePct: p.vs_average_pct == null || isNaN(vs) ? null : vs,
  };
}

// Legend built from the ratings actually present in the response (so its
// colours always match the backend's own hexes), best-first when the rating
// is one of the known three, anything unrecognised after them.
const RATING_ORDER = ["strong", "moderate", "weak"];
export function performanceLegend(perfs: (RegionPerformance | null)[]): { legend: RegionPerformance[]; basis: string | null } {
  const seen = new Map<string, RegionPerformance>();
  perfs.forEach((p) => p && !seen.has(p.rating) && seen.set(p.rating, p));
  const rank = (r: string) => (RATING_ORDER.indexOf(r) === -1 ? RATING_ORDER.length : RATING_ORDER.indexOf(r));
  const legend = Array.from(seen.values()).sort((a, b) => rank(a.rating) - rank(b.rating));
  return { legend, basis: legend.find((p) => p.basis)?.basis ?? null };
}
